import React, { useState } from "react";
import { createPortal } from "react-dom";
import { UserPlus, Trash2, XCircle, Crown, Shield, User } from "lucide-react";
import type { User as UserType, TeamPermissions, TeamRole } from "../types";

const PERMISSION_LABELS: { key: keyof TeamPermissions; label: string }[] = [
  { key: "canViewJobs", label: "Voir les dossiers" },
  { key: "canCreateJobs", label: "Créer des dossiers" },
  { key: "canEditJobs", label: "Modifier les dossiers" },
  { key: "canDeleteJobs", label: "Supprimer des dossiers" },
  { key: "canManageStock", label: "Gérer le stock" },
  { key: "canManagePartners", label: "Gérer les partenaires" },
  { key: "canManageInstallers", label: "Gérer les poseurs" },
  { key: "canManageTeam", label: "Voir l'onglet Équipe" },
];

const AVATAR_COLORS = [
  "bg-emerald-500",
  "bg-violet-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-cyan-500",
  "bg-indigo-500",
];

function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";
}

function avatarColor(id: string): string {
  let n = 0;
  for (let i = 0; i < id.length; i++) n += id.charCodeAt(i);
  return AVATAR_COLORS[n % AVATAR_COLORS.length];
}

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-7 w-12 shrink-0 rounded-full border border-gray-300 dark:border-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
        checked ? "bg-emerald-500" : "bg-gray-300 dark:bg-gray-600"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition translate-y-0.5 ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

interface TeamManagerProps {
  members: UserType[];
  loading: boolean;
  error: string | null;
  canAddMore: boolean;
  /** Seul l'owner peut inviter et retirer des membres. Les membres d'équipe voient la liste en lecture seule. */
  canManageTeam?: boolean;
  onInvite: (data: {
    name: string;
    email: string;
    team_role: TeamRole;
    permissions?: TeamPermissions;
  }) => Promise<{ error: string | null }>;
  onUpdatePermissions: (
    memberId: string,
    permissions: TeamPermissions
  ) => Promise<{ error: string | null }>;
  onRemoveMember: (memberId: string) => Promise<{ error: string | null }>;
}

export function TeamManager({
  members,
  loading,
  error,
  canAddMore,
  canManageTeam = false,
  onInvite,
  onUpdatePermissions,
  onRemoveMember,
}: TeamManagerProps) {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    name: "",
    email: "",
    team_role: "member" as TeamRole,
    permissions: {} as TeamPermissions,
  });
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [editingPermissions, setEditingPermissions] = useState<string | null>(
    null
  );
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const handleRemoveMember = async (member: UserType) => {
    if (!window.confirm(`Retirer ${member.name} de l'équipe ? Il ne pourra plus se connecter au CRM.`)) return;
    setRemoveError(null);
    setRemovingId(member.id);
    const { error } = await onRemoveMember(member.id);
    setRemovingId(null);
    if (error) setRemoveError(error);
  };

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError(null);
    setInviteSubmitting(true);
    const { error: err } = await onInvite({
      name: inviteForm.name,
      email: inviteForm.email,
      team_role: inviteForm.team_role,
      permissions:
        inviteForm.team_role === "member" ? inviteForm.permissions : undefined,
    });
    setInviteSubmitting(false);
    if (err) {
      setInviteError(err);
      return;
    }
    setInviteOpen(false);
    setInviteForm({
      name: "",
      email: "",
      team_role: "member",
      permissions: {},
    });
  };

  const togglePermission = (
    memberId: string,
    key: keyof TeamPermissions,
    value: boolean
  ) => {
    const member = members.find((m) => m.id === memberId);
    const current = member?.permissions ?? {};
    const next = { ...current, [key]: value };
    onUpdatePermissions(memberId, next);
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800 p-8 text-center text-gray-500 dark:text-slate-400">
        Chargement de l&apos;équipe...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800 p-8 text-center text-red-500 dark:text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <p className="text-gray-600 dark:text-slate-300 text-sm">
          {members.length} personne{members.length !== 1 ? "s" : ""} dans
          l&apos;équipe (max. 3)
        </p>
        {canAddMore && (
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 hover:opacity-95 text-white font-medium shadow-lg shadow-blue-500/30 transition-opacity"
          >
            <UserPlus size={20} /> Inviter un membre
          </button>
        )}
      </div>

      {removeError && (
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-500/20 border border-red-200 dark:border-red-500/40 text-red-700 dark:text-red-200 text-sm flex justify-between items-center gap-2">
          <span>{removeError}</span>
          <button type="button" onClick={() => setRemoveError(null)} className="p-1 rounded-lg text-red-500 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/20">
            <XCircle size={18} />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {members.map((member) => {
          const isOwner = member.is_owner === true;
          const isMember = member.team_role === "member";
          const canEdit = !isOwner && isMember;
          const showRemove = canManageTeam && !isOwner;

          return (
            <div
              key={member.id}
              className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800 backdrop-blur-xl dark:backdrop-blur-xl shadow-xl dark:shadow-2xl overflow-hidden"
            >
              <div className="p-5">
                <div className="flex items-start gap-4">
                  <div
                    className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${avatarColor(
                      member.id
                    )}`}
                  >
                    {getInitials(member.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 dark:text-white truncate">
                        {member.name}
                      </span>
                      {isOwner && (
                        <span className="inline-flex items-center gap-1 text-xs bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-500/30">
                          <Crown size={12} /> Owner
                        </span>
                      )}
                      {member.team_role === "admin" && !isOwner && (
                        <span className="inline-flex items-center gap-1 text-xs bg-violet-100 dark:bg-violet-500/20 text-violet-800 dark:text-violet-300 px-2 py-0.5 rounded-full border border-violet-200 dark:border-violet-500/30">
                          <Shield size={12} /> Admin
                        </span>
                      )}
                      {member.team_role === "member" && (
                        <span className="inline-flex items-center gap-1 text-xs bg-gray-100 dark:bg-slate-500/20 text-gray-700 dark:text-slate-300 px-2 py-0.5 rounded-full border border-gray-200 dark:border-white/10">
                          <User size={12} /> Membre
                        </span>
                      )}
                    </div>
                    {member.email && (
                      <p className="text-sm text-gray-500 dark:text-slate-400 truncate mt-0.5">
                        {member.email}
                      </p>
                    )}

                    {canEdit && canManageTeam && (
                      <div className="mt-4 space-y-2">
                        <button
                          type="button"
                          onClick={() =>
                            setEditingPermissions(
                              editingPermissions === member.id ? null : member.id
                            )
                          }
                          className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {editingPermissions === member.id
                            ? "Masquer les droits"
                            : "Configurer les droits"}
                        </button>
                        {editingPermissions === member.id && (
                          <div className="pt-2 space-y-2 border-t border-gray-200 dark:border-white/10">
                            {PERMISSION_LABELS.map(({ key, label }) => (
                              <div
                                key={key}
                                className="flex items-center justify-between gap-2"
                              >
                                <span className="text-sm text-gray-600 dark:text-slate-300">
                                  {label}
                                </span>
                                <Toggle
                                  checked={
                                    !!(member.permissions ?? {})[key]
                                  }
                                  onChange={(v) =>
                                    togglePermission(member.id, key, v)
                                  }
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Bouton supprimer bien visible en haut à droite */}
                  {showRemove && (
                    <button
                      type="button"
                      title="Retirer de l'équipe"
                      disabled={removingId === member.id}
                      onClick={() => handleRemoveMember(member)}
                      className="flex-shrink-0 p-2 rounded-xl text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/20 border border-red-200 dark:border-red-500/30 transition-colors disabled:opacity-50"
                    >
                      <Trash2 size={20} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modale d'invitation (rendue dans un portail pour passer au-dessus du header) */}
      {inviteOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-xl animate-in fade-in duration-200"
            onClick={() => !inviteSubmitting && setInviteOpen(false)}
          >
            <div
              className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800 dark:backdrop-blur-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200"
              onClick={(e) => e.stopPropagation()}
            >
            <div className="p-6 text-gray-900 dark:text-white">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Inviter un membre</h3>
                <button
                  type="button"
                  onClick={() =>
                    !inviteSubmitting && setInviteOpen(false)
                  }
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <XCircle size={22} />
                </button>
              </div>

              <form onSubmit={handleInviteSubmit} className="space-y-4">
                {inviteError && (
                  <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl px-3 py-2">
                    {inviteError}
                  </p>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Nom
                  </label>
                  <input
                    type="text"
                    required
                    value={inviteForm.name}
                    onChange={(e) =>
                      setInviteForm((f) => ({ ...f, name: e.target.value }))
                    }
                    className="w-full px-4 py-2 rounded-xl bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Jean Dupont"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={inviteForm.email}
                    onChange={(e) =>
                      setInviteForm((f) => ({ ...f, email: e.target.value }))
                    }
                    className="w-full px-4 py-2 rounded-xl bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="jean@exemple.fr"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Rôle (1 Second Admin et/ou des Membres, max 2 au total)
                  </label>
                  <select
                    value={inviteForm.team_role}
                    onChange={(e) =>
                      setInviteForm((f) => ({
                        ...f,
                        team_role: e.target.value as TeamRole,
                      }))
                    }
                    className="w-full px-4 py-2 rounded-xl bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="admin" className="bg-white dark:bg-slate-800 text-gray-900 dark:text-white">
                      Second Admin — tous les droits (comme vous)
                    </option>
                    <option value="member" className="bg-white dark:bg-slate-800 text-gray-900 dark:text-white">
                      Membre — droits configurables
                    </option>
                  </select>
                </div>

                {inviteForm.team_role === "member" && (
                  <div className="pt-2 border-t border-gray-200 dark:border-white/10 space-y-2">
                    <p className="text-sm font-medium text-gray-700 dark:text-slate-300">
                      Permissions
                    </p>
                    {PERMISSION_LABELS.map(({ key, label }) => (
                      <div
                        key={key}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className="text-sm text-gray-600 dark:text-slate-300">{label}</span>
                        <Toggle
                          checked={!!inviteForm.permissions[key]}
                          onChange={(v) =>
                            setInviteForm((f) => ({
                              ...f,
                              permissions: {
                                ...f.permissions,
                                [key]: v,
                              },
                            }))
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => !inviteSubmitting && setInviteOpen(false)}
                    className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 font-medium transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={inviteSubmitting}
                    className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 hover:opacity-95 text-white font-medium shadow-lg shadow-blue-500/30 disabled:opacity-50 transition-opacity"
                  >
                    {inviteSubmitting ? "Envoi..." : "Inviter"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
